import { useEffect, useRef } from 'react';

/**
 * Autoguardado con espera: junta los cambios y dispara cuando pasan `ms` sin
 * tocar nada.
 *
 * Estaba escrito dentro de `AutomationTab.tsx` y lo usaba solo esa pantalla;
 * pasó acá cuando lo necesitaron también las cotizaciones y las facturas
 * (REGLA #13: si dos pantallas lo comparten, vive en un solo lugar).
 *
 * `flushOnUnmount` es lo que hace que salir de la pantalla no pierda lo último
 * tecleado: sin eso, escribir y navegar dentro de la ventana de espera se lleva
 * el cambio sin decir nada.
 */
export function useDebouncedFlush(flush: () => void, ms = 800, flushOnUnmount = false) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(flush);
  fnRef.current = flush;
  // Si hay algo esperando al desmontar, se manda. El ref no cambia entre
  // renders, así que el efecto corre una sola vez y su limpieza es la del
  // desmontaje real.
  const pendiente = useRef(false);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (flushOnUnmount && pendiente.current) fnRef.current();
  // `flushOnUnmount` no cambia en la vida del componente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    queue() {
      pendiente.current = true;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => { pendiente.current = false; fnRef.current(); }, ms);
    },
    now() {
      if (timer.current) clearTimeout(timer.current);
      pendiente.current = false;
      fnRef.current();
    },
    cancel() {
      if (timer.current) clearTimeout(timer.current);
      pendiente.current = false;
    },
  };
}
